import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { callApi, HttpMethod } from "src/services/apiService";
import { toast } from "sonner";
import Loading from "src/components/Loading";
import GoHighLevelNav from "./GoHighLevelNav";
import LocationSettingsForm from "./LocationSettingsForm";
import styles from "./GoHighLevel.module.css";

interface LocationDetail {
  locationId: string;
  locationName: string;
  isActive: boolean;
  contactCount: number | null;
  contactCountUpdatedAt: string | null;
  agencyName: string;
  companyId: string;
  bulkScrubEnabled: boolean;
  bulkScrubSchedule: string | null;
  bulkScrubTimeZone: string | null;
  bulkScrubLastRunAt: string | null;
  rescrubIntervalHours: number;
  rescrubIntervalValue: number | null;
  rescrubIntervalUnit: string | null;
  scrubLimit: number | null;
}

const formatRescrubInterval = (detail: LocationDetail): string => {
  if (detail.rescrubIntervalValue == null || !detail.rescrubIntervalUnit)
    return `${detail.rescrubIntervalHours} Hours`;

  const unit = detail.rescrubIntervalValue === 1
    ? detail.rescrubIntervalUnit.replace(/s$/, "")
    : detail.rescrubIntervalUnit;
  return `${detail.rescrubIntervalValue} ${unit}`;
};

interface SingleScrub {
  id: number;
  scrubbedAt: string;
  contactId: string;
  source: string | null;
  phoneCount: number;
  worstStatus: string | null;
}

interface BulkRun {
  id: number;
  status: number;
  processedCount: number | null;
  updatedCount: number | null;
  skippedCount: number | null;
  startedAt: string;
  completedAt: string | null;
  error: string | null;
}

const STATUS_LABEL: Record<number, string> = { 0: "Queued", 1: "Processing", 2: "Completed", 3: "Failed" };
const STATUS_CLASS: Record<number, string> = { 0: "bg-secondary", 1: "bg-info", 2: "bg-success", 3: "bg-danger" };

const SubAccountDetail: React.FC = () => {
  const { locationId = "" } = useParams();
  const [detail, setDetail] = useState<LocationDetail | null>(null);
  const [scrubs, setScrubs] = useState<SingleScrub[]>([]);
  const [runs, setRuns] = useState<BulkRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [scrubsUsed, setScrubsUsed] = useState<number | null>(null);
  const [usedLoading, setUsedLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setDetail(null);
    setScrubs([]);
    setRuns([]);
    try {
      const result = await callApi(`sysadmin/gohighlevel/locations/${locationId}/detail`, HttpMethod.GET);
      setDetail(result.location);
      setScrubs(result.recentScrubs);
      setRuns(result.recentBulkRuns);
    } catch {
      toast.error("Failed to load subaccount detail.");
    } finally {
      setLoading(false);
    }
  }, [locationId]);

  useEffect(() => {
    load();
  }, [load]);

  // Loaded separately from the main detail: the usage count is slow on first hit (then Redis-cached
  // ~1h), so only this tile shows a spinner instead of blocking the whole page.
  const loadScrubsUsed = useCallback(async () => {
    setUsedLoading(true);
    setScrubsUsed(null);
    try {
      const result = await callApi(`sysadmin/gohighlevel/locations/${locationId}/scrubs-used`, HttpMethod.GET);
      setScrubsUsed(result.scrubsUsed);
    } catch {
      setScrubsUsed(null);
    } finally {
      setUsedLoading(false);
    }
  }, [locationId]);

  useEffect(() => {
    loadScrubsUsed();
  }, [loadScrubsUsed]);

  if (loading) return <Loading />;
  if (!detail)
    return (
      <div className={styles.container}>
        <GoHighLevelNav />
        <div className={styles.emptyState}>Couldn't load this sub-account. Please try again.</div>
      </div>
    );

  const lastRun = runs[0];

  return (
    <div className={styles.container}>
      <GoHighLevelNav />
      <Link
        to={`/sysadmin/gohighlevel/agencies/${detail.companyId}`}
        className={styles.backLink}
      >
        &larr; {detail.agencyName}
      </Link>
      <div className={styles.pageHeader}>
        <div>
          <h2 className={styles.pageTitle}>{detail.locationName || detail.locationId}</h2>
          <p className={styles.pageSubtitle}>{detail.agencyName}</p>
        </div>
      </div>

      <div className={styles.statGrid}>
        <StatBox value={detail.contactCount != null ? detail.contactCount.toLocaleString() : "—"} label="Contacts"
          sub={detail.contactCountUpdatedAt ? `as of ${new Date(detail.contactCountUpdatedAt).toLocaleDateString()}` : "never refreshed"} />
        <StatBox value={detail.bulkScrubEnabled ? "On" : "Off"} label="Bulk scrub"
          sub={detail.bulkScrubEnabled ? `${detail.bulkScrubSchedule || ""} ${detail.bulkScrubTimeZone || "UTC"}` : undefined} />
        <StatBox value={detail.bulkScrubLastRunAt ? new Date(detail.bulkScrubLastRunAt).toLocaleDateString() : "Never"} label="Last bulk scrub"
          sub={detail.bulkScrubLastRunAt ? new Date(detail.bulkScrubLastRunAt).toLocaleTimeString() : undefined} />
        <StatBox value={detail.scrubLimit != null ? detail.scrubLimit.toLocaleString() : "—"} label="Scrubs Included in Plan" />
        <StatBox
          value={usedLoading
            ? <span className="spinner-border spinner-border-sm text-secondary" role="status" aria-hidden="true" />
            : (scrubsUsed != null ? scrubsUsed.toLocaleString() : "—")}
          label="Scrubs used (this month)"
        />
        <StatBox value={formatRescrubInterval(detail)} label="Re-scrub interval" />
        {lastRun && (
          <StatBox
            value={lastRun.processedCount != null ? lastRun.processedCount.toLocaleString() : "—"}
            label="Last run processed"
            sub={`${lastRun.updatedCount ?? 0} updated · ${lastRun.skippedCount ?? 0} skipped`}
          />
        )}
      </div>

      <div className={styles.detailGrid}>
        <div>
          <div className={styles.sectionTitle}>Last 10 scrubs</div>
          {scrubs.length === 0 ? (
            <div className={styles.emptyState}>No individual scrubs recorded yet.</div>
          ) : (
            <table className={styles.miniTable}>
              <thead>
                <tr>
                  <th>When</th>
                  <th>Contact</th>
                  <th>Source</th>
                  <th style={{ textAlign: "right" }}>Phones</th>
                  <th>Worst status</th>
                </tr>
              </thead>
              <tbody>
                {scrubs.map((s) => (
                  <tr key={s.id}>
                    <td>{new Date(s.scrubbedAt).toLocaleString()}</td>
                    <td>{s.contactId}</td>
                    <td>{s.source || "—"}</td>
                    <td style={{ textAlign: "right" }}>{s.phoneCount}</td>
                    <td>{s.worstStatus || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div className={styles.sectionTitle}>Recent bulk runs</div>
          {runs.length === 0 ? (
            <div className={styles.emptyState}>No bulk scrub runs yet.</div>
          ) : (
            <table className={styles.miniTable}>
              <thead>
                <tr>
                  <th>Started</th>
                  <th>Status</th>
                  <th style={{ textAlign: "right" }}>Processed</th>
                  <th style={{ textAlign: "right" }}>Updated</th>
                  <th style={{ textAlign: "right" }}>Skipped</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((r) => (
                  <tr key={r.id}>
                    <td>{new Date(r.startedAt).toLocaleString()}</td>
                    <td>
                      <span className={`badge ${STATUS_CLASS[r.status] ?? "bg-secondary"}`}>{STATUS_LABEL[r.status] ?? "Unknown"}</span>
                      {r.error && <span className={styles.formHint} title={r.error}> ⚠</span>}
                    </td>
                    <td style={{ textAlign: "right" }}>{r.processedCount?.toLocaleString() ?? "—"}</td>
                    <td style={{ textAlign: "right" }}>{r.updatedCount?.toLocaleString() ?? "—"}</td>
                    <td style={{ textAlign: "right" }}>{r.skippedCount?.toLocaleString() ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div>
          <div className={styles.sectionTitle}>Settings</div>
          <LocationSettingsForm locationId={locationId} onSaved={load} />
        </div>
      </div>
    </div>
  );
};

const StatBox: React.FC<{ value: React.ReactNode; label: string; sub?: string }> = ({ value, label, sub }) => (
  <div className={styles.statCard}>
    <div className={styles.statValue}>{value}</div>
    <div className={styles.statLabel}>{label}</div>
    {sub && <div className={styles.statSub}>{sub}</div>}
  </div>
);

export default SubAccountDetail;
